import React, { FC, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingResource, pricingResourceItem } from "../../../api/endpoints";
import { LicenseRow, usePricing } from "./PricingContext";
import { SnapshotBanner } from "./SnapshotBanner";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";

export const Licenses: FC = () => {
    const { data, loading, refetch, canEdit } = usePricing();
    const [busyId, setBusyId] = useState<number | null>(null);
    const [draft, setDraft] = useState({ key: "", display_name: "", price: "" });

    if (loading || !data) return <DotLottleLoader />;

    const handlePatch = async (id: number, patch: Partial<LicenseRow>) => {
        setBusyId(id);
        try {
            await axiosInstant.patch(pricingResourceItem("licenses", id), patch);
            toast.success("Saved");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id: number) => {
        setBusyId(id);
        try {
            await axiosInstant.delete(pricingResourceItem("licenses", id));
            toast.success("Deleted");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to delete");
        } finally {
            setBusyId(null);
        }
    };

    const handleCreate = async () => {
        if (!draft.key.trim() || !draft.display_name.trim()) {
            toast.error("Key and display name are required");
            return;
        }
        const priceNum = Number(draft.price);
        if (!Number.isFinite(priceNum) || priceNum < 0) {
            toast.error("Price must be a non-negative number");
            return;
        }
        try {
            await axiosInstant.post(pricingResource("licenses"), {
                key: draft.key.trim(),
                display_name: draft.display_name.trim(),
                price: priceNum
            });
            toast.success("License added");
            setDraft({ key: "", display_name: "", price: "" });
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to add license");
        }
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Licenses
            </Typography>
            <SnapshotBanner />
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Key</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Display Name</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Price</strong>
                            </TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.licenses.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        defaultValue={row.key}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value !== row.key && e.target.value.trim()) {
                                                if (
                                                    window.confirm(
                                                        "Changing the license key affects how existing projects reference it. Continue?"
                                                    )
                                                ) {
                                                    handlePatch(row.id, { key: e.target.value.trim() });
                                                } else {
                                                    e.target.value = row.key;
                                                }
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        defaultValue={row.display_name}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value !== row.display_name && e.target.value.trim()) {
                                                handlePatch(row.id, { display_name: e.target.value.trim() });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        defaultValue={row.price}
                                        helperText={formatCurrency(row.price)}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            const v = Number(e.target.value);
                                            if (Number.isFinite(v) && v !== Number(row.price)) {
                                                handlePatch(row.id, { price: v });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(row.id)}
                                            disabled={busyId === row.id}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {canEdit && (
                        <TableRow>
                            <TableCell>
                                <TextField
                                    size="small"
                                    placeholder="2-year"
                                    value={draft.key}
                                    onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    placeholder="2 Year License"
                                    value={draft.display_name}
                                    onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder="0"
                                    value={draft.price}
                                    onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreate}
                                    disabled={!draft.key.trim() || !draft.display_name.trim()}
                                >
                                    Add
                                </Button>
                            </TableCell>
                        </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Licenses;
